import { useEffect, useState } from "react";
import HandleConfirmationPopup from "../HandleConfirmation/HandleConfirmationPopup";
import { ICourt } from "../../types/api";
import {
  IOpeningHoursConflict,
  cancelConflict,
  conflictColumns,
  conflictKey,
} from "./openingHoursConflicts";
import "./OpeningHoursConflictConfirmation.scss";

interface IOpeningHoursConflictConfirmationProps {
  showPopup: boolean;
  onClose: () => void;
  /** Go ahead with the opening-hours change. */
  onConfirm: () => void;
  conflicts: IOpeningHoursConflict[];
  /** Only used to turn a court_id into "B4". */
  courts: ICourt[];
  /**
   * The lookup itself failed, so an empty conflicts list means "unknown", not
   * "none". Blocking on an unknown would turn any hiccup in the query into an
   * outage for the whole opening-hours screen, so the old hedged warning is
   * shown instead and the change is allowed through.
   */
  checkFailed?: boolean;
}

/**
 * The second confirmation when changing opening hours.
 *
 * A booking outside the new hours does not get cancelled — it stays in the
 * summary and simply stops being shown, so the member still believes they
 * have a court and nobody is told otherwise. Rather than warn about that and
 * let it happen anyway, the change is blocked until every affected booking has
 * been dealt with, which can be done from this list.
 *
 * Shared by the create and edit popups: both ask the same question.
 */
function OpeningHoursConflictConfirmation(
  props: IOpeningHoursConflictConfirmationProps
) {
  const { showPopup, onClose, onConfirm, conflicts, courts, checkFailed } =
    props;

  const [cancelled, setCancelled] = useState<Set<string>>(new Set());
  const [cancelling, setCancelling] = useState<string>();
  const [sendCancellationEmail, setSendCancellationEmail] = useState(true);
  // Asked about before anything happens — an avbokning cannot be undone, and
  // the rows sit close enough together to mis-click.
  const [pendingCancel, setPendingCancel] = useState<IOpeningHoursConflict>();

  useEffect(() => {
    if (!showPopup) return;
    setCancelled(new Set());
    setPendingCancel(undefined);
  }, [showPopup]);

  const handleCancelConflict = async (conflict: IOpeningHoursConflict) => {
    const key = conflictKey(conflict);
    setCancelling(key);
    try {
      if (await cancelConflict(conflict, sendCancellationEmail)) {
        setCancelled((prev) => new Set(prev).add(key));
      }
    } finally {
      setCancelling(undefined);
    }
  };

  const outstanding = conflicts.filter(
    (conflict) => !cancelled.has(conflictKey(conflict))
  );
  const blocked = outstanding.length > 0;
  // Activities are not bookings and there is no resolver to cancel one from
  // here, so they can only be cleared in the booking view. Called out
  // separately because otherwise the disabled button looks broken.
  const blockingActivities = outstanding.filter(
    (conflict) => !conflict.cancel
  ).length;

  const questionText = () => {
    if (checkFailed) {
      return "Bokningarna kunde inte kontrolleras. Om bokningar ligger utanför uppdaterade öppettider kommer dessa inte avbokas. Vill du genomföra uppdateringen ändå?";
    }
    if (blocked) {
      return `${outstanding.length} ${
        outstanding.length === 1 ? "bokning ligger" : "bokningar ligger"
      } utanför de nya öppettiderna och måste avbokas innan du kan spara.`;
    }
    if (conflicts.length > 0) {
      return "Alla berörda bokningar är avbokade. Vill du spara de nya öppettiderna?";
    }
    return "Inga bokningar ligger utanför de nya öppettiderna. Vill du genomföra uppdateringen?";
  };

  // Keyed off conflicts rather than blocked, so the label does not change
  // under the cursor as rows are cancelled.
  const confirmText = conflicts.length > 0 ? "Spara" : "Ja";

  const pendingColumns = pendingCancel
    ? conflictColumns(pendingCancel, courts)
    : undefined;

  return (
    <>
      <HandleConfirmationPopup
        showPopup={showPopup}
        onClose={onClose}
        onConfirm={onConfirm}
        confirmText={confirmText}
        denyText="Avbryt"
        wide={conflicts.length > 0}
        confirmDisabled={blocked}
        questionText={questionText()}
      >
        {conflicts.length > 0 && (
          <>
            <div className="opening-hours-conflicts-email">
              <span className="text-s secondary-text">
                Skicka avbokningsmejl till medlemmen
              </span>
              <label className="toggle text-m">
                <input
                  type="checkbox"
                  checked={sendCancellationEmail}
                  onChange={(e) => setSendCancellationEmail(e.target.checked)}
                />
                <span className="slider round"></span>
              </label>
            </div>
            <ul className="opening-hours-conflicts text-m">
              {conflicts.map((conflict) => {
                const key = conflictKey(conflict);
                const isCancelled = cancelled.has(key);
                const columns = conflictColumns(conflict, courts);
                return (
                  <li key={key} className={isCancelled ? "is-cancelled" : ""}>
                    <span className="conflict-category text-s secondary-text">
                      {columns.category}
                    </span>
                    <span className="conflict-court">{columns.court}</span>
                    <span className="conflict-time">{columns.time}</span>
                    <span className="conflict-who">{columns.who}</span>
                    {isCancelled ? (
                      <span className="conflict-action text-s secondary-text">
                        Avbokad
                      </span>
                    ) : (
                      <span className="conflict-action">
                        {conflict.cancel ? (
                          <a
                            className={`text-s text-danger ${
                              cancelling ? "" : "pointer"
                            }`}
                            onClick={() =>
                              !cancelling && setPendingCancel(conflict)
                            }
                          >
                            {cancelling === key ? "Avbokar…" : "Avboka"}
                          </a>
                        ) : (
                          <span className="text-s secondary-text">
                            Aktivitet
                          </span>
                        )}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
            {blockingActivities > 0 && (
              <p className="text-s secondary-text opening-hours-conflicts-note">
                Aktiviteter kan inte avbokas härifrån. Ta bort dem i bokningsvyn
                först.
              </p>
            )}
          </>
        )}
      </HandleConfirmationPopup>

      <HandleConfirmationPopup
        showPopup={!!pendingCancel}
        onClose={() => setPendingCancel(undefined)}
        onConfirm={() => {
          if (pendingCancel) handleCancelConflict(pendingCancel);
          setPendingCancel(undefined);
        }}
        leftTopElement={<span className="text-m">Avboka bokning</span>}
        confirmText="Avboka"
        denyText="Avbryt"
        questionText={
          pendingColumns
            ? `Avboka ${pendingColumns.category} ${
                pendingColumns.court === "–" ? "" : pendingColumns.court + " "
              }${pendingColumns.time} — ${pendingColumns.who}?`
            : ""
        }
      >
        <p className="text-s secondary-text">
          {sendCancellationEmail
            ? "Ett avbokningsmejl skickas till medlemmen."
            : "Inget avbokningsmejl skickas."}
        </p>
      </HandleConfirmationPopup>
    </>
  );
}

export default OpeningHoursConflictConfirmation;
